import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useEffect,
} from 'react';
import { ToastContainer } from 'react-bootstrap';
import ReactDOM from 'react-dom/client';

import BayToast from './BayToastNew';

interface ToastOptions {
	text?: string;
	variant?: string;
	className?: string;
	loading?: boolean;
	externalLinkText?: string;
	onExternalLinkClick?: () => void;
	delay?: number;
	autoHide?: boolean;
	dismissible?: boolean;
	addLineBreak?: boolean;
}

interface Toast extends ToastOptions {
	id: number;
	show: boolean;
}

interface ToastContextValue {
	showToast: (options: ToastOptions) => number;
	hideToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
	children: React.ReactNode;
	position?: 'top-start' | 'top-center' | 'top-end' | 'middle-start' | 'middle-center' | 'middle-end' | 'bottom-start' | 'bottom-center' | 'bottom-end';
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, position = 'top-end' }) => {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const [root, setRoot] = useState<ReactDOM.Root | null>(null);

	useEffect(() => {
		const toastRoot = document.createElement('div');
		toastRoot.id = 'toast-root';
		document.body.appendChild(toastRoot);

		const rootInstance = ReactDOM.createRoot(toastRoot);
		setRoot(rootInstance);

		return () => {
			rootInstance.unmount();
			document.body.removeChild(toastRoot);
		};
	}, []);

	const showToast = useCallback((options: ToastOptions) => {
		const id = Date.now() + Math.random();
		const toast: Toast = {
			id,
			show: true,
			delay: 5000,
			autoHide: true,
			dismissible: true,
			...options,
		};

		setToasts((prevToasts) => [...prevToasts, toast]);

		if (toast.autoHide) {
			setTimeout(() => hideToast(id), toast.delay);
		}

		return id;
	}, []);

	const hideToast = useCallback((id: number) => {
		setToasts((prevToasts) =>
			prevToasts.map((toast) =>
				toast.id === id ? { ...toast, show: false } : toast,
			),
		);
	}, []);

	const removeToast = useCallback((id: number) => {
		setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
	}, []);

	useEffect(() => {
		if (root) {
			root.render(
				<ToastContainer
					bsPrefix="toast-container"
					className="position-fixed p-3"
					position={position}
				>
					{toasts.map((toast) => (
						<BayToast
							key={toast.id}
							{...toast}
							className={`bay-toast-variant-${toast.variant} ${
								toast.className ?? ''
							}`}
							onClose={() => hideToast(toast.id)}
							onExited={() => removeToast(toast.id)}
						/>
					))}
				</ToastContainer>,
			);
		}
	}, [toasts, root, hideToast, removeToast, position]);

	return (
		<ToastContext.Provider value={{ showToast, hideToast }}>
			{children}
		</ToastContext.Provider>
	);
};

export const useToast = (): ToastContextValue => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error('useToast must be used within a ToastProvider');
	}
	return context;
};
